import React from 'react';
import {useShallow} from 'zustand/react/shallow';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import type {QuestionModelV3} from '@albert-io/models';
import LiveRegion from 'lib/LiveRegion';
import Divider from './Divider.react';
import {Item} from './Item.react';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import classNames from 'classnames';
import {getItemAccessibleName, useItemAccessibleName} from '../../shared/accessibleName';
import {useAnnouncer} from '../../shared/useAnnouncer';
import {indexAfterMove} from '../store';
import {deselectedMessage, insertPointLabel, movedMessage, selectedMessage} from './announcements';
interface Props {
  question: QuestionModelV3;
}
export const ItemList = ({question}: Props) => {
  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {saveDraftGuess} = useQuestionAndDrafts(question);

  const {
    activeItemId,
    draggedItemId,
    items,
    isDragging,
    hoveredDividerId,
    setActiveItemId,
    moveItemToPosition,
    isSubmitted
    // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  } = useStoreHook(
    useShallow((state) => ({
      items: state.items,
      activeItemId: state.activeItemId,
      draggedItemId: state.draggedItemId,
      isDragging: state.isDragging,
      hoveredDividerId: state.hoveredDividerId,
      setActiveItemId: state.setActiveItem,
      setHoveredDividerId: state.setHoveredDividerId,
      moveItemToPosition: state.moveItemToPosition,
      isSubmitted: state.isSubmitted
    }))
  );

  const {message, announce} = useAnnouncer();
  const total = items.length;
  const indexOf = (itemId: string | null) => items.findIndex((item) => item.id === itemId);

  const movingIndex = indexOf(activeItemId ?? draggedItemId);
  const movingItemName = useItemAccessibleName(
    movingIndex === -1 ? null : items[movingIndex].value
  );
  const dividerLabel = (dividerIndex: number) =>
    movingIndex === -1
      ? 'Insert position'
      : insertPointLabel(movingItemName, movingIndex, dividerIndex, total);

  const handleItemClick = (itemId: string, _e?: React.MouseEvent) => {
    if (isDragging) return;

    const index = indexOf(itemId);
    const name = index === -1 ? 'Item' : getItemAccessibleName(items[index].value);
    if (activeItemId === itemId) {
      setActiveItemId(null);
      announce(deselectedMessage(name));
    } else {
      setActiveItemId(itemId);
      announce(selectedMessage(name, index, total));
    }
  };

  const handleDividerClick = (dividerIndex: number) => {
    if (isDragging || !activeItemId) return;

    const fromIndex = indexOf(activeItemId);
    moveItemToPosition(activeItemId, dividerIndex);
    saveDraftGuess();
    setActiveItemId(null);
    if (fromIndex !== -1) {
      const name = getItemAccessibleName(items[fromIndex].value);
      announce(movedMessage(name, fromIndex, indexAfterMove(fromIndex, dividerIndex), total));
    }
  };

  return (
    <>
      <div
        className={classNames(
          'flex flex-wrap items-center',
          {
            'gap-x3 pl-x3': isSubmitted,
            'gap-y-x2 pl-x0': !isSubmitted
          },
          'rounded-x1 border border-solid border-border-primary background-primary p-x2'
        )}
      >
        {items.map((item, index) => (
          <Item
            key={item.id}
            item={item}
            index={index}
            dividerLabel={dividerLabel(index)}
            handleItemClick={handleItemClick}
            handleDividerClick={handleDividerClick}
            question={question}
          />
        ))}
        {/* Trailing divider for inserting after the last item */}
        {!isSubmitted && (
          <Divider
            id={`divider-${items.length}`}
            label={dividerLabel(items.length)}
            isVisible={isDragging || activeItemId !== null}
            isHovered={hoveredDividerId === `divider-${items.length}`}
            onClick={() => handleDividerClick(items.length)}
          />
        )}
      </div>
      <LiveRegion assertive={false} className='sr-only'>
        {message}
      </LiveRegion>
    </>
  );
};

export default ItemList;
